import './ImportModal.scss';
import { library } from '@fortawesome/fontawesome-svg-core';
import { faUpload } from '@fortawesome/free-solid-svg-icons';
import { TranslationKey, useTranslator } from '@Localization';
import { Button } from '@mui/joy';
import classNames from 'classnames';
import { observer } from 'mobx-react-lite';
import { ReactElement, useMemo } from 'react';
import { Feedback } from '../../../../__legacy__/components/Feedback';
import { ValidatedTextArea } from '../../../../__legacy__/components/ValidatedTextArea';
import { ValidatedTextInput } from '../../../../__legacy__/components/ValidatedTextInput';
import { ButtonWithLoader } from '../../../../design/buttons/ButtonWithLoader';
import { S_Dropzone } from '../../../../design/dropzone/S_Dropzone';
import { S_FieldLabel } from '../../../../design/labels/fieldLabel/S_FIeldLabel';
import { S_Modal } from '../../../../design/modals/modal/S_Modal';
import { ImportValidationErrorsContainer } from '../../containers/list/ImportValidationErrors.container';
import { FileStripe } from './FileStripe';

library.add(faUpload);

interface IImportModalProps {
  showModal: boolean;
  data: {
    name: string;
    description: string;
    file: File | null;
    errors: {
      showStatus: boolean;
      name?: TranslationKey;
    };
    acceptedFiles: File[];
    rejectedFiles: File[];
  };
  isLoading: boolean;

  toggleModal: () => void;
  handleImageDrop: (files: File[]) => void;
  handleChangeTextField(key: string): (value: string) => void;
  removeSelectedFile: () => void;
  handleSubmit: () => Promise<void>;
}

export const ImportModal = observer((props: IImportModalProps): ReactElement => {
  const { t } = useTranslator();
  const isReadyToSubmit = useMemo(() => !!props.data.name && !!props.data.file, [props.data]);
  const hasValidFiles = props.data.acceptedFiles.length > 0;
  const hasInvalidFiles = props.data.rejectedFiles.length > 0;

  return (
    <S_Modal
      className='model-import-modal'
      isOpen={props.showModal}
      toggle={props.toggleModal}
      headerText={t('training.import.button')}
      buttons={
        <>
          <ButtonWithLoader
            loading={props.isLoading}
            variant='outlined'
            color='neutral'
            id='cancel-button'
            onClick={props.toggleModal}
          >
            {t('cancel')}
          </ButtonWithLoader>
          <ButtonWithLoader
            disabled={!isReadyToSubmit}
            loading={props.isLoading}
            onClick={() => {
              void props.handleSubmit();
            }}
          >
            {t('import')}
          </ButtonWithLoader>{' '}
        </>
      }
    >
      <div className='models-import-note'>{t('training.import.info')}</div>
      <div>
        <S_FieldLabel
          text={t('training.name.title')}
          isRequired
        />
        <ValidatedTextInput
          id='model-name-textbox'
          type='text'
          placeholder={t('training.name.placeholder')}
          value={props.data.name}
          onChange={props.handleChangeTextField('name')}
          isValid={props.data.errors.showStatus && props.data.errors.name !== undefined ? props.data.errors.name.length === 0 : undefined}
          feedbacks={[props.data.errors.name ?? '']}
        />
      </div>
      <div>
        <S_FieldLabel text={t('training.description.title')} />
        <ValidatedTextArea
          id='model-name-textbox'
          placeholder={t('training.description.placeholder')}
          value={props.data.description}
          onChange={props.handleChangeTextField('description')}
        />
      </div>
      <ImportValidationErrorsContainer />
      {hasValidFiles ? (
        <FileStripe
          fileName={props.data.acceptedFiles[0].name}
          onRemoveFileClicked={props.removeSelectedFile}
        />
      ) : (
        <div>
          <S_Dropzone
            multiple={false}
            className={classNames({ 'is-invalid': hasInvalidFiles })}
            onDrop={props.handleImageDrop}
            accept={{ 'application/zip': ['.zip'] }}
          >
            <div className='drop-zone-content'>
              <div className='upper-title'>{t('training.import.title')}</div>
              <div className='title-conjunction'>{t('or')}</div>
              <div>
                <Button variant='outlined'>{t('training.import.select_files')}</Button>
              </div>
            </div>
          </S_Dropzone>
          {hasInvalidFiles && <Feedback>{t('training.import.invalid_file_type')}</Feedback>}
        </div>
      )}
    </S_Modal>
  );
});
